import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useI18n } from "@/context/I18nContext";

const catImages = {
  foodstuffs: "https://images.unsplash.com/photo-1783013958606-889689019517?w=300&q=80",
  fashion: "https://images.unsplash.com/photo-1773858441059-d741dc584aa7?w=300&q=80",
  cosmetics: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=300&q=80",
  "wine-alcohol": "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=300&q=80",
  beverages: "https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=300&q=80",
  health: "https://images.unsplash.com/photo-1505576399279-565b52d4ac71?w=300&q=80",
  handmade: "https://images.unsplash.com/photo-1610701596007-11502861dcfa?w=300&q=80",
  agriculture: "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=300&q=80",
  electronics: "https://images.unsplash.com/photo-1498049794561-7780e7231661?w=300&q=80",
  "europe-used-items": "https://images.unsplash.com/photo-1637228393246-c38a4b3d2011?w=300&q=80",
  "food-grocery": "https://images.unsplash.com/photo-1783013958606-889689019517?w=300&q=80",
  "custom-products": "https://images.pexels.com/photos/7319172/pexels-photo-7319172.jpeg?auto=compress&cs=tinysrgb&w=300",
  "wigs-accessories": "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=300&q=80",
  "mens-clothing": "https://images.unsplash.com/photo-1773858441059-d741dc584aa7?w=300&q=80",
  "womens-clothing": "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=300&q=80",
  "mens-shoes": "https://images.pexels.com/photos/1027130/pexels-photo-1027130.jpeg?auto=compress&cs=tinysrgb&w=300",
  "womens-shoes": "https://images.pexels.com/photos/1027130/pexels-photo-1027130.jpeg?auto=compress&cs=tinysrgb&w=300",
  bags: "https://images.pexels.com/photos/7953286/pexels-photo-7953286.jpeg?auto=compress&cs=tinysrgb&w=300",
  "library-books": "https://images.unsplash.com/photo-1512820790803-83ca734da794?w=300&q=80",
  "auto-spare-parts": "https://images.unsplash.com/photo-1759419281480-bacc913c9606?w=300&q=80",
  furniture: "https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?w=300&q=80",
  "building-materials": "https://images.pexels.com/photos/36003985/pexels-photo-36003985.jpeg?auto=compress&cs=tinysrgb&w=300",
};

export default function MobileCategory() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCats(r.data);
      if (r.data.length) setActive(r.data[0].slug);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/products?category=${active}&limit=30`).then((r) => setProducts(r.data));
  }, [active]);

  const activeCat = cats.find((c) => c.slug === active);

  return (
    <Layout hideAd>
      <div className="max-w-7xl mx-auto flex" data-testid="mobile-category-page">
        {/* Left rail */}
        <aside className="w-28 sm:w-40 shrink-0 border-r border-border/60 bg-muted/20 min-h-[70vh]">
          {cats.map((c) => (
            <button
              key={c.slug}
              data-testid={`cat-rail-${c.slug}`}
              onClick={() => setActive(c.slug)}
              className={`w-full text-left px-3 py-4 text-sm leading-tight border-l-4 transition-colors ${active === c.slug ? "border-primary bg-background font-semibold text-primary" : "border-transparent text-muted-foreground hover:bg-background/60"}`}
            >
              {c.name}
            </button>
          ))}
        </aside>

        {/* Right content */}
        <section className="flex-1 p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-display font-bold text-xl">{activeCat?.name || t("categories")}</h1>
            <button onClick={() => activeCat && navigate(`/products?category=${activeCat.slug}`)} data-testid="cat-viewall" className="text-muted-foreground"><ChevronRight className="w-5 h-5" /></button>
          </div>

          {products.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">No products in this category yet.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-4">
              {products.map((p) => (
                <button
                  key={p.product_id}
                  data-testid={`cat-prod-${p.product_id}`}
                  onClick={() => navigate(`/product/${p.product_id}`)}
                  className="group text-center"
                >
                  <div className="aspect-square rounded-full overflow-hidden mb-2 border border-border/50 bg-muted group-hover:border-primary/50 transition-all">
                    <img src={(p.images && p.images[0]) || catImages[p.category]} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <span className="text-xs font-medium leading-tight block line-clamp-2">{p.title}</span>
                  <span className="text-xs font-bold text-primary">€{(p.discount_price || p.price).toFixed(2)}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
